import {computed, ref} from 'vue'
import {useStore} from 'vuex'
import APIClient from '@/service/APIClient'
import {UserRole} from '@/models/enum/UserRole'
import {getSystemSettingById, parseListSettingValue} from '@/service/SystemSettingsHelper'

export const COUNTERPARTY_MANAGEMENT_ROLES_SETTING_ID = 'COUNTERPARTY_MANAGEMENT_ROLES'

const DEFAULT_MANAGEMENT_ROLES = [UserRole.ADMIN, UserRole.OPERATOR]

const CANDIDATE_ROLES = [
    UserRole.ADMIN,
    UserRole.OPERATOR,
    UserRole.TRADER,
    UserRole.TRADER_OPERATOR,
    UserRole.TRADER_TEAM_LEAD,
]

const managementRoles = ref<string[] | null>(null)
const rolesLoaded = ref(false)
const accessDeniedByApi = ref(false)
let loadPromise: Promise<void> | null = null

function loadRoles(store: ReturnType<typeof useStore>): Promise<void> {
    if (loadPromise) {
        return loadPromise
    }

    loadPromise = (async () => {
        const fromApp = store.getters.getAppData?.COUNTERPARTY_MANAGEMENT_ROLES
        if (Array.isArray(fromApp) && fromApp.length) {
            managementRoles.value = fromApp
            rolesLoaded.value = true
            return
        }

        try {
            const api = new APIClient()
            const settings = await api.systemSettings()
            const setting = getSystemSettingById(settings, COUNTERPARTY_MANAGEMENT_ROLES_SETTING_ID)
            const parsed = setting?.value ? parseListSettingValue(setting.value) : []
            managementRoles.value = parsed.length ? parsed : DEFAULT_MANAGEMENT_ROLES
        } catch {
            managementRoles.value = null
        } finally {
            rolesLoaded.value = true
        }
    })()

    return loadPromise
}

export function useCounterpartyManagementAccess() {
    const store = useStore()
    void loadRoles(store)

    const userRole = computed(() => store.getters.getUser?.role as string | undefined)

    const canManageCounterparties = computed(() => {
        if (store.getters.getAppData?.STORE_COUNTERPARTY_ALLOWLIST_ENABLED === false) {
            return false
        }
        if (accessDeniedByApi.value) {
            return false
        }
        const role = userRole.value
        if (!role) {
            return false
        }
        if (managementRoles.value) {
            return managementRoles.value.includes(role)
        }
        return CANDIDATE_ROLES.includes(role as UserRole)
    })

    const markAccessDenied = () => {
        accessDeniedByApi.value = true
    }

    return {
        canManageCounterparties,
        rolesLoaded,
        accessDeniedByApi,
        markAccessDenied,
    }
}
